import { useParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InvoiceStatusBadge } from "../components/InvoiceStatusBadge";
import { useDownloadSalesInvoicePdf, useSalesInvoiceById, useViewSalesInvoicePdf } from "../hooks/useInvoicing";
import { useNotification } from "@/shared/hooks/useNotification";

const money = (value: number) =>
  new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES" }).format(value || 0);

/** Build a user-friendly discount label for display */
const getDiscountLabel = (discountSource: string | undefined | null): string => {
  if (!discountSource || discountSource === "None") return "";
  if (discountSource === "Campaign") {
    return "Campaign";
  } else if (discountSource === "Individual" || discountSource === "Customer") {
    return "Individual";
  } else if (discountSource === "GroupDefault" || discountSource === "Group") {
    return "Group Default";
  }
  return discountSource;
};

export const SalesInvoiceDetailPage = () => {
  const { id } = useParams();
  const notification = useNotification();
  const invoiceQuery = useSalesInvoiceById(id);
  const viewSalesInvoicePdf = useViewSalesInvoicePdf();
  const downloadSalesInvoicePdf = useDownloadSalesInvoicePdf();

  if (invoiceQuery.isLoading) {
    return <div className="py-10 text-muted-foreground">Loading sales invoice...</div>;
  }

  if (invoiceQuery.error || !invoiceQuery.data) {
    return <div className="py-10 text-red-600">Failed to load sales invoice.</div>;
  }

  const invoice = invoiceQuery.data;

  const handleViewInvoice = () => {
    if (!id) return;
    viewSalesInvoicePdf.mutate(id, {
      onError: () => notification.error("Failed to open invoice PDF"),
    });
  };

  const handleDownloadInvoice = () => {
    if (!id) return;
    downloadSalesInvoicePdf.mutate(id, {
      onError: () => notification.error("Failed to download invoice PDF"),
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <CardTitle className="flex flex-wrap items-center gap-3">
            <span>Sales Invoice {invoice.invoiceNumber}</span>
            <InvoiceStatusBadge statusName={invoice.statusName} isOverdue={invoice.isOverdue} />
            {invoice.orderNumber && <Badge variant="outline">Order {invoice.orderNumber}</Badge>}
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleViewInvoice} disabled={viewSalesInvoicePdf.isPending}>
              View Invoice
            </Button>
            <Button variant="outline" onClick={handleViewInvoice} disabled={viewSalesInvoicePdf.isPending}>
              Print Invoice
            </Button>
            <Button onClick={handleDownloadInvoice} disabled={downloadSalesInvoicePdf.isPending}>
              Download PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3 text-sm">
          <div>Customer: {invoice.customerName}</div>
          <div>Order #: {invoice.orderNumber || "-"}</div>
          <div>Terms: {invoice.terms || "-"}</div>
          <div>Invoice Date: {new Date(invoice.invoiceDate).toLocaleDateString()}</div>
          <div>Due Date: {new Date(invoice.dueDate).toLocaleDateString()}</div>
          <div>Issued By: {invoice.issuedByUserName || "-"}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Financial Summary</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3 text-sm">
          {(invoice.orderDiscountAmount ?? 0) > 0 && (
            <>
              <div>Original (goods): {money(invoice.orderOriginalAmount ?? 0)}</div>
              <div className="text-emerald-700">
                Customer discount
                {(() => {
                  const label = getDiscountLabel(invoice.orderDiscountSource);
                  return label ? ` (${label}` : "";
                })()}
                {(invoice.orderDiscountPercentage ?? 0) > 0 ? ` ${invoice.orderDiscountPercentage!.toFixed(2)}%` : ""}
                {invoice.orderDiscountSource ? ")" : ""}: −{money(invoice.orderDiscountAmount ?? 0)}
              </div>
            </>
          )}
          <div>Subtotal: {money(invoice.subtotal)}</div>
          <div>Tax: {money(invoice.taxAmount)}</div>
          <div>Discount: {money(invoice.discountAmount)}</div>
          <div>Total: {money(invoice.totalAmount)}</div>
          <div>Paid: {money(invoice.paidAmount)}</div>
          <div>Balance: {money(invoice.balanceAmount)}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Line Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Variety</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Unit Price</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Line Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="break-words max-w-xs">{item.description}</TableCell>
                    <TableCell>{item.varietyName || item.variety || "-"}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>{money(item.unitPrice || 0)}</TableCell>
                    <TableCell>{money(item.discountAmount || 0)}</TableCell>
                    <TableCell>{money(item.lineTotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
